import { createAction, props } from '@ngrx/store';
import {
  AuthCredentials,
  AuthResponse,
  User,
  UserProfileRequestDTO,
  UserRequestDTO,
  UserResponseDTO,
} from './models';
import { HttpErrorResponse } from '@angular/common/http';

export const login = createAction(
  '[Auth API] Login User',
  props<{ credentials: AuthCredentials }>(),
);

export const loginSuccess = createAction(
  '[Auth API] Login User Success',
  props<{ response: AuthResponse }>(),
);

export const loginFailed = createAction(
  '[Auth API] Login User Failed',
  props<{ err: HttpErrorResponse }>(),
);

export const logout = createAction(
  '[Auth API] Logout User',
  props<{ redirect: boolean }>(),
);

export const register = createAction(
  '[User API] Register User',
  props<{ user: UserRequestDTO }>(),
);

export const registerSuccess = createAction(
  '[User API] Register User Success',
  props<{
    user: Partial<User>;
    autoLogin?: boolean;
    loginCredentials?: AuthCredentials;
  }>(),
);

export const registerFailed = createAction(
  '[User API] Register User Failed',
  props<{ err: HttpErrorResponse }>(),
);

// Registration v2 (three-step flow)
export const registerIdentify = createAction(
  '[Register API] Identify User',
  props<{ username: string }>(),
);

export const registerIdentifySuccess = createAction(
  '[Register API] Identify User Success',
);

export const registerIdentifyFailed = createAction(
  '[Register API] Identify User Failed',
  props<{ err: HttpErrorResponse }>(),
);

export const registerVerify = createAction(
  '[Register API] Verify User',
  props<{ username: string; code: string }>(),
);

export const registerVerifySuccess = createAction(
  '[Register API] Verify User Success',
  props<{ registrationToken: string }>(),
);

export const registerVerifyFailed = createAction(
  '[Register API] Verify User Failed',
  props<{ err: HttpErrorResponse }>(),
);

export const registerComplete = createAction(
  '[Register API] Complete Registration',
  props<{
    token: string;
    password: string;
    firstName: string;
    lastName: string;
    role: string;
    dateOfBirth?: Date;
    parentEmail?: string;
    inviteToken?: string;
  }>(),
);

export const registerCompleteSuccess = createAction(
  '[Register API] Complete Registration Success',
  props<{ response?: AuthResponse; message?: string }>(),
);

export const registerCompleteFailed = createAction(
  '[Register API] Complete Registration Failed',
  props<{ err: HttpErrorResponse }>(),
);

export const sendVerificationCode = createAction(
  '[User API] Send Verification code',
  props<{ username?: string }>(),
);

export const sendVerificationCodeSuccess = createAction(
  '[User API] Send Verification code Success',
);

export const sendVerificationCodeFailed = createAction(
  '[User API] Send Verification code failed',
  props<{ err: HttpErrorResponse }>(),
);

export const verifyUser = createAction(
  '[User API] Verify user',
  props<{ code: string }>(),
);

export const userVerificationSuccess = createAction(
  '[User API] User verification success',
);

export const userVerificationFailed = createAction(
  '[User API] User verification failed',
  props<{ err: any }>(),
);

// Password reset flow

export const initiateResetPasswordFlow = createAction(
  '[Rest Password Components] Initiate reset password flow',
);

export const sendResetPasswordRequest = createAction(
  '[User API] Send reset password request',
  props<{ username: string; code: string }>(),
);

export const resetPasswordRequestSuccess = createAction(
  '[User API] Reset password request success',
  props<{ resetToken: string }>(),
);

export const resetPasswordRequestFailed = createAction(
  '[User API] Reset password request failed',
  props<{ err: any }>(),
);

export const resetPassword = createAction(
  '[User API] Reset password',
  props<{ token: string; password: string }>(),
);

export const resetPasswordSuccess = createAction(
  '[User API] Reset password success',
);

export const resetPasswordFailed = createAction(
  '[User API] Reset password failed',
  props<{ err: any }>(),
);

// Update Profile
export const updateUserProfile = createAction(
  '[User API] Update User Profile',
  props<{
    userProfile: UserProfileRequestDTO;
    redirectOnSuccess: boolean;
  }>(),
);

export const updateUserProfileSuccess = createAction(
  '[User API] Update User Profile Success',
  props<{ user: UserResponseDTO; redirect: boolean }>(),
);

export const updateUserProfileFailed = createAction(
  '[User API] Update User Profile Failed',
);

export const clearUserState = createAction(
  '[Auth Components] Clear User State',
);

// Rehydration
export const rehydrateUserState = createAction(
  '[App Initialization] Rehydrate User State',
  props<{ userState: any }>(),
);

// Impersonation flow
export const requestImpersonation = createAction(
  '[Admin] Request Impersonation',
  props<{ userId: string }>(),
);

export const requestImpersonationSuccess = createAction(
  '[Admin] Request Impersonation Success',
);

export const requestImpersonationFailed = createAction(
  '[Admin] Request Impersonation Failed',
  props<{ err: HttpErrorResponse }>(),
);

export const verifyImpersonation = createAction(
  '[Admin] Verify Impersonation',
  props<{ userId: string; otp: string }>(),
);

export const verifyImpersonationSuccess = createAction(
  '[Admin] Verify Impersonation Success',
  props<{ token: string; user: UserResponseDTO }>(),
);

export const verifyImpersonationFailed = createAction(
  '[Admin] Verify Impersonation Failed',
  props<{ err: HttpErrorResponse }>(),
);

export const endImpersonation = createAction('[Admin] End Impersonation');

export const endImpersonationSuccess = createAction(
  '[Admin] End Impersonation Success',
  props<{ user: UserResponseDTO | null }>(),
);

// Tutors Public list & Profiles
export const loadTutors = createAction(
  '[Tutor API] Load Tutors',
  props<{
    page: number;
    limit: number;
    filters?: {
      search?: string;
      subject?: string;
      medium?: string;
      classFormat?: string;
      district?: string;
    };
  }>(),
);

export const loadTutorsSuccess = createAction(
  '[Tutor API] Load Tutors Success',
  props<{ response: any }>(),
);

export const loadTutorsFailed = createAction(
  '[Tutor API] Load Tutors Failed',
  props<{ err: HttpErrorResponse }>(),
);

export const loadTutorProfile = createAction(
  '[Tutor API] Load Tutor Profile',
  props<{ identifier: string }>(),
);

export const loadTutorProfileSuccess = createAction(
  '[Tutor API] Load Tutor Profile Success',
  props<{
    response: { tutor: UserResponseDTO; courses: any[]; liveClasses: any[] };
  }>(),
);

export const loadTutorProfileFailed = createAction(
  '[Tutor API] Load Tutor Profile Failed',
  props<{ err: HttpErrorResponse }>(),
);

export const updateTutorProfile = createAction(
  '[Tutor API] Update Tutor Profile',
  props<{ tutorInfo: any }>(),
);

export const updateTutorProfileSuccess = createAction(
  '[Tutor API] Update Tutor Profile Success',
  props<{ user: UserResponseDTO }>(),
);

export const updateTutorProfileFailed = createAction(
  '[Tutor API] Update Tutor Profile Failed',
  props<{ err: HttpErrorResponse }>(),
);

export const deleteUserAccount = createAction('[User API] Delete User Account');

export const deleteUserAccountSuccess = createAction(
  '[User API] Delete User Account Success',
);

export const deleteUserAccountFailed = createAction(
  '[User API] Delete User Account Failed',
  props<{ err: any }>(),
);
